"use client";

import { useEffect, useRef, useState } from "react";
import Script from "next/script";
import { phCapture } from "@/utils/analytics";
import {
  REWARDFUL_RETRY_DELAY_MS,
  rewardfulLoadAttempt,
  rewardfulPhaseAfterError,
  rewardfulScriptSrc,
  type RewardfulLoadPhase,
} from "@/lib/rewardful-script-load";

const REWARDFUL_BOOT_DELAY_MS = 3500;

export function RewardfulScripts() {
  const rewardfulSiteId = "527d15";
  const [phase, setPhase] = useState<RewardfulLoadPhase>("waiting");
  const [scriptGeneration, setScriptGeneration] = useState(0);
  const retryTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const startTimer = () => {
      const timer = window.setTimeout(() => {
        setPhase("loading");
      }, REWARDFUL_BOOT_DELAY_MS);

      return () => window.clearTimeout(timer);
    };

    if (document.readyState === "complete") {
      return startTimer();
    }

    let cleanupTimer: (() => void) | undefined;
    const handleLoad = () => {
      cleanupTimer = startTimer();
    };

    window.addEventListener("load", handleLoad, { once: true });

    return () => {
      window.removeEventListener("load", handleLoad);
      cleanupTimer?.();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current);
      }
    };
  }, []);

  const shouldLoadRewardful = phase === "loading" || phase === "retrying";

  return (
    <>
      <Script id="rewardful-queue" strategy="beforeInteractive">
        {`
          (function(w,r){
            w._rwq = r;
            w[r] = w[r] || function(){ (w[r].q = w[r].q || []).push(arguments) };
          })(window,'rewardful');
        `}
      </Script>
      {shouldLoadRewardful ? (
        <Script
          key={`rewardful-main-${scriptGeneration}`}
          id="rewardful-main"
          src={rewardfulScriptSrc(scriptGeneration)}
          data-rewardful={rewardfulSiteId}
          strategy="lazyOnload"
          onError={() => {
            const nextPhase = rewardfulPhaseAfterError(phase);
            try {
              phCapture("rewardful_script_load_failed", {
                surface: "rewardful_scripts",
                attempt: rewardfulLoadAttempt(phase),
              });
            } catch {
            }
            setPhase(nextPhase);
            if (nextPhase === "retrying") {
              retryTimerRef.current = window.setTimeout(() => {
                setScriptGeneration((generation) => generation + 1);
              }, REWARDFUL_RETRY_DELAY_MS);
            }
          }}
          onLoad={() => {
            setPhase("loaded");
            try {
              phCapture("rewardful_script_loaded", {
                surface: "rewardful_scripts",
                attempt: rewardfulLoadAttempt(phase),
              });
            } catch {
            }
          }}
        />
      ) : null}
    </>
  );
}
